import React from "react";
import {
  isChunkLoadError,
  reloadOnceForChunkError,
} from "../../helpers/staleBuild";

interface GenericErrorBoundaryTypes {
  children: React.ReactNode;
  headless?: boolean;
}

class GenericErrorBoundary extends React.Component<GenericErrorBoundaryTypes> {
  state: {
    hasError: boolean;
    chunkLoadError: boolean;
  };

  constructor(props: GenericErrorBoundaryTypes) {
    super(props);
    this.state = { hasError: false, chunkLoadError: false };
  }

  componentDidCatch(error, info) {
    // Display fallback UI
    const chunkLoadError = isChunkLoadError(error);
    this.setState({ hasError: true, chunkLoadError });
    // The notice below only stays visible when no automatic reload happens
    if (chunkLoadError) reloadOnceForChunkError();
    // You can also log the error to an error reporting service
    // logErrorToMyService(error, info);
    window.debug("Caught at ErrorBoundary", error, info);

    // Dispatch event for E2E test tracking
    if (typeof CustomEvent !== "undefined") {
      document.dispatchEvent(
        new CustomEvent("react-error-boundary", {
          detail: {
            message: error?.message || String(error),
            stack: error?.stack,
            componentStack: info?.componentStack,
          },
        }),
      );
    }
  }

  render() {
    if (this.state.hasError && this.props.headless) return null;

    if (this.state.chunkLoadError) {
      return (
        <div className="alert alert-info flex flex-col items-start gap-2">
          {I18n.t("js.chunk_load_error.message")}
          <button
            type="button"
            className="btn btn-light"
            onClick={() => window.location.reload()}
          >
            {I18n.t("js.chunk_load_error.reload")}
          </button>
        </div>
      );
    }

    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className="alert alert-error">{I18n.t("js.generic_error")}</div>
      );
    }
    return this.props.children;
  }
}

export default GenericErrorBoundary;
